import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import API from '../api/config';
import { MessageCircle, CheckCircle2, Clock, Send, Loader2, ChevronDown, ChevronUp, User } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_TABS = ['ALL', 'OPEN', 'REPLIED', 'RESOLVED'];

const STATUS_STYLE = {
  OPEN:     'bg-amber-50 text-amber-700 border-amber-200',
  REPLIED:  'bg-blue-50 text-blue-700 border-blue-200',
  RESOLVED: 'bg-forest-50 text-forest-700 border-forest-200',
};

const STATUS_ICON = {
  OPEN:     <Clock size={11} />,
  REPLIED:  <Send size={11} />,
  RESOLVED: <CheckCircle2 size={11} />,
};

// ── Single Request Card ───────────────────────────────────────
const RequestCard = ({ req, onReply, onStatusChange }) => {
  const [expanded,  setExpanded]  = useState(false);
  const [reply,     setReply]     = useState('');
  const [sending,   setSending]   = useState(false);

  const userName = `${req.user?.firstName ?? ''} ${req.user?.lastName ?? ''}`.trim();
  const initials = `${req.user?.firstName?.[0] ?? ''}${req.user?.lastName?.[0] ?? ''}`;
  const date     = new Date(req.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleReply = async () => {
    if (!reply.trim()) { toast.error('Reply cannot be empty'); return; }
    setSending(true);
    try {
      await onReply(req.id, reply.trim());
      setReply('');
      toast.success('Reply sent to user!');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-start gap-4 p-5 cursor-pointer hover:bg-cream-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Avatar */}
        <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0 overflow-hidden">
          {req.user?.avatar
            ? <img src={req.user.avatar} alt={userName} className="w-full h-full object-cover" />
            : initials}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-forest-900 text-sm">{userName}</span>
            <span className="text-xs text-forest-400">{req.user?.email}</span>
            <span className="text-[10px] bg-cream-100 text-forest-500 px-2 py-0.5 rounded-full capitalize">
              {req.user?.role?.toLowerCase().replace('_', ' ')}
            </span>
          </div>
          <p className="font-semibold text-forest-900 text-sm mt-0.5">{req.subject}</p>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-xs text-forest-400">{date}</span>
            {req.category && (
              <span className="text-xs text-forest-400">· {req.category}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`flex items-center gap-1 text-[10px] font-semibold border px-2.5 py-1 rounded-full ${STATUS_STYLE[req.status]}`}>
            {STATUS_ICON[req.status]} {req.status}
          </span>
          {expanded ? <ChevronUp size={14} className="text-forest-400" /> : <ChevronDown size={14} className="text-forest-400" />}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-cream-100 px-5 pb-5 pt-4 space-y-4">

          {/* User message */}
          <div>
            <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-2">User's Message</p>
            <div className="bg-cream-50 border border-cream-200 rounded-xl px-4 py-3 text-sm text-forest-700 leading-relaxed whitespace-pre-wrap">
              {req.message}
            </div>
          </div>

          {/* Previous admin reply */}
          {req.adminReply && (
            <div>
              <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-2">Your Previous Reply</p>
              <div className="bg-forest-50 border border-forest-200 rounded-xl px-4 py-3 text-sm text-forest-700 leading-relaxed whitespace-pre-wrap">
                {req.adminReply}
              </div>
              {req.repliedAt && (
                <p className="text-xs text-forest-400 mt-1">
                  Replied {new Date(req.repliedAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              )}
            </div>
          )}

          {/* Reply box */}
          {req.status !== 'RESOLVED' && (
            <div>
              <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-2">
                {req.adminReply ? 'Send another reply' : 'Reply to user'}
              </p>
              <textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                rows={4}
                placeholder={`Type your reply to ${req.user?.firstName}...`}
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none"
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={handleReply}
                  disabled={sending || !reply.trim()}
                  className="btn-primary flex items-center gap-2 text-sm disabled:opacity-60"
                >
                  {sending
                    ? <><Loader2 size={13} className="animate-spin" /> Sending...</>
                    : <><Send size={13} /> Send Reply</>}
                </button>
                <button
                  onClick={() => onStatusChange(req.id, 'RESOLVED')}
                  className="px-4 py-2 rounded-full border border-forest-200 text-forest-600 text-xs font-medium hover:bg-forest-50 transition-colors flex items-center gap-1.5"
                >
                  <CheckCircle2 size={13} /> Mark Resolved
                </button>
                {req.status === 'OPEN' && (
                  <button
                    onClick={() => onStatusChange(req.id, 'REPLIED')}
                    className="px-4 py-2 rounded-full border border-cream-200 text-forest-500 text-xs font-medium hover:bg-cream-50 transition-colors"
                  >
                    Mark as Replied
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Resolved state */}
          {req.status === 'RESOLVED' && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-forest-500 text-sm">
                <CheckCircle2 size={15} className="text-forest-600" /> Marked as resolved
              </div>
              <button
                onClick={() => onStatusChange(req.id, 'OPEN')}
                className="text-xs text-forest-400 hover:text-forest-700 transition-colors"
              >
                Reopen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Admin Page ───────────────────────────────────────────
const AdminHelpPage = () => {
  const [activeTab,  setActiveTab]  = useState('ALL');
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [counts,     setCounts]     = useState({ ALL: 0, OPEN: 0, REPLIED: 0, RESOLVED: 0 });

  const fetchRequests = async (status) => {
    setLoading(true);
    try {
      const params = status !== 'ALL' ? `?status=${status}` : '';
      const res    = await API.get(`/help${params}`);
      const data   = res.data?.data ?? res.data;
      setRequests(data?.requests ?? []);
    } catch {
      toast.error('Could not load help requests');
    } finally { setLoading(false); }
  };

  const fetchCounts = async () => {
    try {
      const [all, open, replied, resolved] = await Promise.all([
        API.get('/help'),
        API.get('/help?status=OPEN'),
        API.get('/help?status=REPLIED'),
        API.get('/help?status=RESOLVED'),
      ]);
      setCounts({
        ALL:      all.data?.data?.total      ?? 0,
        OPEN:     open.data?.data?.total     ?? 0,
        REPLIED:  replied.data?.data?.total  ?? 0,
        RESOLVED: resolved.data?.data?.total ?? 0,
      });
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchRequests(activeTab);
  }, [activeTab]);

  useEffect(() => {
    fetchCounts();
  }, []);

  const handleReply = async (id, reply) => {
    await API.post(`/help/${id}/reply`, { reply });
    fetchRequests(activeTab);
    fetchCounts();
  };

  const handleStatusChange = async (id, status) => {
    try {
      await API.patch(`/help/${id}/status`, { status });
      toast.success(`Marked as ${status.toLowerCase()}`);
      fetchRequests(activeTab);
      fetchCounts();
    } catch {
      toast.error('Could not update status');
    }
  };

  return (
    <DashboardLayout title="Help Requests">
      <div className="max-w-4xl space-y-5">

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total',    count: counts.ALL,      color: 'text-forest-900' },
            { label: 'Open',     count: counts.OPEN,     color: 'text-amber-600'  },
            { label: 'Replied',  count: counts.REPLIED,  color: 'text-blue-600'   },
            { label: 'Resolved', count: counts.RESOLVED, color: 'text-forest-600' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
              <div className={`font-serif text-3xl font-bold ${s.color}`}>{s.count}</div>
              <div className="text-xs text-forest-500 mt-1 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs font-medium px-4 py-2 rounded-full border transition-all ${
                activeTab === tab
                  ? 'bg-forest-800 text-cream-50 border-forest-800'
                  : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'
              }`}
            >
              {tab} {counts[tab] > 0 && `(${counts[tab]})`}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-forest-600" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
            <MessageCircle size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No help requests</p>
            <p className="text-sm mt-1">
              {activeTab === 'ALL' ? 'No one has submitted a help request yet.' : `No ${activeTab.toLowerCase()} requests.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <RequestCard
                key={req.id}
                req={req}
                onReply={handleReply}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default AdminHelpPage;